import { useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { showToast } from '@/contexts/ToastContext';
import { hasStringValue } from '@/utils/validation';
import { getCurrentTimeZone, convertInputDateToServerTime, convertInputDateToEndOfDayServerTime } from '@/utils/date-time';
import { displayNocValuesForGridTemplate } from '@/utils/formatting';
import { b64toBlob } from '@/utils/blob';
import { isUWPApp, isIOSApp, isAndroidApp } from '@/utils/browser';
import { notifyApp } from '@/services/app-notify';
import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Dropdownlist } from '@/components/Dropdownlist';
import { KstDataGrid } from '@/components/KstDataGrid';

export function CallsForServiceScreen() {
  const { callsForServiceNpsdClientList } = useAuth();

  const [eventId, setEventId] = useState('');
  const [location, setLocation] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(callsForServiceNpsdClientList.length > 0 ? callsForServiceNpsdClientList[0].id : '');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [gridEntries, setGridEntries] = useState<any[]>([]);
  const [gridPager, setGridPager] = useState({ curPage: 1, numPages: 1, records: 0, isVisible: false, pageSize: 20 });
  const [gridLoading, setGridLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState<Record<number, boolean>>({});
  const [activePickerKey, setActivePickerKey] = useState<string | null>(null);
  const pageSize = 20;

  const onPickerOpenChange = useCallback((key: string, isOpen: boolean) => {
    if (isOpen) setActivePickerKey(key);
    else if (activePickerKey === key) setActivePickerKey(null);
  }, [activePickerKey]);

  const getCallCardFilter = useCallback(() => {
    const filter: any = { logic: 'and', filters: [] };
    const filters = [
      { field: 'eventId', operator: 'startswith', value: eventId.trim() },
      { field: 'callLocFormattedAddress', operator: 'contains', value: location.trim() },
      { field: 'npsdClientId', operator: 'eq', value: selectedClient },
      { field: 'callDateTime', operator: 'gte', value: startDate ? convertInputDateToServerTime(startDate) : null },
      { field: 'callDateTime', operator: 'lte', value: endDate ? convertInputDateToEndOfDayServerTime(endDate) : null },
    ];
    for (const elt of filters) {
      if (hasStringValue(elt.value)) filter.filters.push(elt);
    }
    return filter;
  }, [eventId, location, selectedClient, startDate, endDate]);

  const loadGrid = useCallback(async (page: number) => {
    setGridLoading(true);
    setSearching(true);
    try {
      const result = await api.callCardSearch(page, pageSize, { data: { filter: getCallCardFilter() } });
      setGridEntries(result.entries || []);
      const records = result.records || 0;
      const numPages = Math.max(1, Math.ceil(records / pageSize));
      setGridPager({ curPage: page, numPages, records, isVisible: records > 0, pageSize });
      setTimeout(() => {
        document.getElementById('callsForServiceSearchResultsCard')?.scrollIntoView();
      }, 0);
    } catch {
      // Error handled by API
    }
    setGridLoading(false);
    setSearching(false);
  }, [getCallCardFilter]);

  const handleSearch = useCallback(() => {
    setShowResults(true);
    loadGrid(1);
  }, [loadGrid]);

  const handleGoToPage = useCallback((page: number) => {
    loadGrid(page);
  }, [loadGrid]);

  const handleCellClick = useCallback(async ({ row, column }: any) => {
    if (column.field !== 'pdf') return;
    if (pdfDownloading[row.id]) return;
    setPdfDownloading(prev => ({ ...prev, [row.id]: true }));
    try {
      const data = { callCardId: row.id, timeZoneId: getCurrentTimeZone() };
      const result = await api.getCallCardPdfBase64(data);
      const pdfBase64String: string = result.pdfBase64;
      const filename: string = result.pdfFilename;
      if (isUWPApp || isIOSApp || isAndroidApp) {
        notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
      } else {
        const blob = b64toBlob(pdfBase64String, 'application/pdf');
        saveAs(blob, filename);
      }
    } catch {
      showToast('Error downloading Calls for Service PDF.', 'error');
    }
    setPdfDownloading(prev => ({ ...prev, [row.id]: false }));
  }, [pdfDownloading]);

  const columns = useMemo(() => [
    {
      field: 'pdf', name: 'PDF', template(row: any) {
        return "<a class='callCardPdfDownload' href='#'><i class='fas fa-download'></i></a>";
      },
    },
    { field: 'eventId', name: 'Event ID' },
    {
      field: 'callDateTime', name: 'Date/Time', template(row: any) {
        if (!row.callDateTime) return '';
        return DateTime.fromISO(row.callDateTime).toFormat('dd MMM yyyy  HH:mm');
      },
    },
    {
      field: 'natureOfCallValues', name: 'Nature of Call', template(row: any) {
        return displayNocValuesForGridTemplate(row);
      },
    },
    { field: 'callLocFormattedAddress', name: 'Event Location' },
  ], []);

  return (
    <>
      <div className="kstc-card kst-card-width">
        <div className="kstc-card__body">
          <h5 className="kstc-card__title">Calls For Service</h5>
          <p className="kstc-card__text">Search Calls For Service.</p>
          <div className="kstc-row kstc-mb-3 kstc-gy-3">
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Event ID</label>
              <input type="text" className="kstc-input" value={eventId} onChange={(e) => setEventId(e.target.value)} />
            </div>
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Location</label>
              <input type="text" className="kstc-input" value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Client</label>
              <Dropdownlist value={selectedClient} items={callsForServiceNpsdClientList} labelKey="name" valueKey="id" placeholder="Select Client" sortItems={false} onChange={(val) => setSelectedClient(val)} />
            </div>
          </div>
          <div className="kstc-row kstc-gy-3">
            <div className="kstc-col-sm-6">
              <label className="kstc-label">Start Date (Call Date/Time)</label>
              <DateTimePicker value={startDate} type="date" appendToBody={true} onChange={setStartDate} onOpenChange={(val) => onPickerOpenChange('callsForServiceStart', val)} />
            </div>
            <div className="kstc-col-sm-6">
              <label className="kstc-label">End Date (Call Date/Time)</label>
              <DateTimePicker value={endDate} type="date" appendToBody={true} onChange={setEndDate} onOpenChange={(val) => onPickerOpenChange('callsForServiceEnd', val)} />
            </div>
          </div>
          <div className="kstc-row kstc-mt-3 kstc-gy-3">
            <div className="kstc-col-sm-12">
              <button type="button" disabled={searching} onClick={handleSearch} className="kstc-btn kstc-btn--primary kstc-btn--lg kstc-float-end">
                {!searching ? <span>Search</span> : <><span className="kstc-spinner kstc-spinner--sm" role="status" aria-hidden="true"></span><span> Searching...</span></>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showResults && (
        <div id="callsForServiceSearchResultsCard" className="kstc-card kst-card-width">
          <div className="kstc-card__body">
            <h5 className="kstc-card__title">Calls For Service - Search Results</h5>
            <KstDataGrid
              columns={columns}
              rows={{ entries: gridEntries }}
              pager={gridPager}
              loading={gridLoading}
              showcheckbox={false}
              showheader={false}
              onGoToPage={handleGoToPage}
              onCellClick={handleCellClick}
            />
          </div>
        </div>
      )}
    </>
  );
}
